/**
 * AcceptInviteV2 — v2 reskin of auth/AcceptInvite (UI-v2 Phase 1e).
 *
 * ALL auth logic/hooks/mutations verbatim from v1. Presentation only:
 * AuthShellV2 + shadcn primitives + FormFieldV2. wide=true.
 */
import { useState } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useTenantBase } from "@/hooks/useTenantBase";
import { useInvitePreview, useAcceptInvite } from "@/hooks/queries/auth/useAcceptInvite";
import { useToastStore } from "@/stores/useToastStore";
import { useAuthStore } from "@/stores/useAuthStore";
import { AuthShellV2 } from "@/components/v2/layout/AuthShellV2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { FormFieldV2 } from "@/components/v2/patterns/FormV2";

const PASSWORD_MIN = 12;
const PASSWORD_MAX = 128;

export default function AcceptInviteV2(): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const base = useTenantBase();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const addToast = useToastStore((state) => state.addToast);

  const token = searchParams.get("token") ?? "";

  const preview = useInvitePreview(tenantSlug ?? "", token);
  const acceptInvite = useAcceptInvite();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [aupAccepted, setAupAccepted] = useState(false);
  const [privacyAcknowledged, setPrivacyAcknowledged] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const passwordLengthOk =
    password.length >= PASSWORD_MIN && password.length <= PASSWORD_MAX;
  const confirmMatches = confirmPassword.length > 0 && confirmPassword === password;
  const confirmMismatch = confirmPassword.length > 0 && confirmPassword !== password;

  if (!token) {
    return (
      <AuthShellV2 title="Accept invitation" subtitle="Invalid invite link.">
        <p className="text-center text-sm text-text-secondary">
          Please use the link from your invitation email.
        </p>
      </AuthShellV2>
    );
  }

  if (preview.isLoading) {
    return (
      <AuthShellV2 title="Accept invitation">
        <p className="text-center text-sm text-text-secondary">
          Loading invite details…
        </p>
      </AuthShellV2>
    );
  }

  if (preview.isError || !preview.data) {
    const errCode = preview.error?.error;
    const message =
      errCode === "invalid_token"
        ? "This invite link is invalid or has expired. Ask your admin to send a new one."
        : "Unable to load invite details. Please try again.";
    return (
      <AuthShellV2 title="Accept invitation" subtitle="Invite not found.">
        <p className="text-center text-sm text-text-secondary">{message}</p>
      </AuthShellV2>
    );
  }

  const { email, tenant_name, role } = preview.data;

  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    passwordLengthOk &&
    confirmMatches &&
    aupAccepted &&
    privacyAcknowledged &&
    !acceptInvite.isPending;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFieldErrors({});

    if (!tenantSlug) return;

    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs["firstName"] = "First name is required.";
    if (!lastName.trim()) errs["lastName"] = "Last name is required.";
    if (!passwordLengthOk)
      errs["password"] = `Password must be ${PASSWORD_MIN}–${PASSWORD_MAX} characters.`;
    if (!confirmMatches) errs["confirmPassword"] = "Passwords don't match.";
    if (!aupAccepted) errs["aup"] = "You must accept the Acceptable Use Policy to continue.";
    if (!privacyAcknowledged)
      errs["accountPrivacy"] = "You must acknowledge the Account-Data Privacy Notice to continue.";
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }

    acceptInvite.mutate(
      {
        tenantSlug,
        token,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password,
        aup_accepted: true,
        account_privacy_acknowledged: true,
      },
      {
        onSuccess: (data) => {
          useAuthStore.getState().loginSuccess(data.access_token);
          addToast(`Welcome to ${tenant_name}!`, "success");
          // AU2: land in the v2 shell after accepting an invite from the v2 flow.
          navigate(`${base}/dashboard`, { replace: true });
        },
        onError: (err) => {
          switch (err.error) {
            case "invalid_token":
              setFieldErrors({ form: "This invite link is invalid or has expired." });
              break;
            case "weak_password": {
              const reasons = Array.isArray(err.details["reasons"])
                ? (err.details["reasons"] as string[]).join(", ")
                : "Password is too weak.";
              setFieldErrors({ password: reasons });
              break;
            }
            case "aup_acceptance_required":
              setFieldErrors({ aup: "Acceptable Use Policy acceptance is required." });
              break;
            case "account_privacy_acknowledgment_required":
              setFieldErrors({ accountPrivacy: "Account-Data Privacy Notice acknowledgment is required." });
              break;
            case "workspace_user_limit_reached":
              setFieldErrors({ form: "This workspace has reached its seat limit. Contact your admin." });
              break;
            case "email_in_use":
              setFieldErrors({ form: "An account with this email already exists in this workspace." });
              break;
            default:
              setFieldErrors({ form: "That didn't go through. Try again." });
          }
        },
      }
    );
  };

  return (
    <AuthShellV2
      title={`Join ${tenant_name}`}
      subtitle={`You've been invited as ${role}. Set up your account to continue.`}
      wide
    >
      {/* Invite context — pre-filled email, non-editable */}
      <div
        className="rounded-lg border border-border-default bg-surface-inset text-sm text-text-secondary"
        style={{ padding: "8px 12px", marginBottom: "16px" }}
      >
        Joining as{" "}
        <span className="font-semibold text-text-primary">{email}</span>
      </div>

      {/* Top-level form error */}
      {fieldErrors["form"] && (
        <div
          className="rounded-lg border border-status-rejected-ring bg-status-rejected-bg text-sm text-status-rejected-fg"
          style={{ padding: "8px 12px", marginBottom: "16px" }}
          role="alert"
        >
          {fieldErrors["form"]}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <FormFieldV2
          label="First name"
          htmlFor="firstName-v2"
          error={fieldErrors["firstName"]}
        >
          <Input
            id="firstName-v2"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            aria-invalid={!!fieldErrors["firstName"] || undefined}
          />
        </FormFieldV2>

        <FormFieldV2
          label="Last name"
          htmlFor="lastName-v2"
          error={fieldErrors["lastName"]}
        >
          <Input
            id="lastName-v2"
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            aria-invalid={!!fieldErrors["lastName"] || undefined}
          />
        </FormFieldV2>

        <FormFieldV2
          label="Password"
          htmlFor="password-v2"
          error={fieldErrors["password"]}
        >
          <Input
            id="password-v2"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            aria-invalid={!!fieldErrors["password"] || undefined}
          />
          {/* Live length indicator */}
          <div
            role="status"
            aria-live="polite"
            aria-label="Password requirements"
            className="flex items-center gap-1.5"
            style={{ fontSize: "12px" }}
          >
            <span
              aria-hidden="true"
              className={passwordLengthOk ? "text-verdict-strong-fg" : "text-text-tertiary"}
            >
              {passwordLengthOk ? "✓" : "○"}
            </span>
            <span className={passwordLengthOk ? "text-verdict-strong-fg" : "text-text-secondary"}>
              At least {PASSWORD_MIN} characters
            </span>
          </div>
        </FormFieldV2>

        <FormFieldV2
          label="Confirm password"
          htmlFor="confirmPassword-v2"
          error={fieldErrors["confirmPassword"]}
        >
          <Input
            id="confirmPassword-v2"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            aria-invalid={!!fieldErrors["confirmPassword"] || confirmMismatch || undefined}
            aria-describedby="confirm-pw-status-v2"
          />
          {confirmPassword.length > 0 && (
            <div
              id="confirm-pw-status-v2"
              role="status"
              aria-live="polite"
              className="flex items-center gap-1.5"
              style={{ fontSize: "12px" }}
            >
              <span
                aria-hidden="true"
                className={confirmMatches ? "text-verdict-strong-fg" : "text-verdict-gap-fg"}
              >
                {confirmMatches ? "✓" : "✗"}
              </span>
              <span className={confirmMatches ? "text-verdict-strong-fg" : "text-verdict-gap-fg"}>
                {confirmMatches ? "Passwords match" : "Passwords don't match yet"}
              </span>
            </div>
          )}
        </FormFieldV2>

        {/* Legal checkboxes */}
        <div
          className="flex flex-col gap-2 rounded-lg border border-border-default bg-surface-inset"
          style={{ padding: "12px" }}
        >
          <div className="flex flex-col gap-0.5">
            <Label className="flex cursor-pointer items-start gap-2 font-normal">
              <Checkbox
                id="aup-v2"
                checked={aupAccepted}
                onCheckedChange={(v) => setAupAccepted(!!v)}
                className="mt-0.5"
              />
              <span className="text-text-secondary">
                I accept the{" "}
                <a
                  href="/legal/acceptable-use"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-link underline hover:text-text-link-hover"
                >
                  Acceptable Use Policy
                </a>
              </span>
            </Label>
            {fieldErrors["aup"] && (
              <p className="ml-6 text-verdict-gap-fg" style={{ fontSize: "12px" }}>
                {fieldErrors["aup"]}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-0.5">
            <Label className="flex cursor-pointer items-start gap-2 font-normal">
              <Checkbox
                id="privacy-v2"
                checked={privacyAcknowledged}
                onCheckedChange={(v) => setPrivacyAcknowledged(!!v)}
                className="mt-0.5"
              />
              <span className="text-text-secondary">
                I acknowledge the{" "}
                <a
                  href="/legal/account-data-privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-link underline hover:text-text-link-hover"
                >
                  Account-Data Privacy Notice
                </a>
              </span>
            </Label>
            {fieldErrors["accountPrivacy"] && (
              <p className="ml-6 text-verdict-gap-fg" style={{ fontSize: "12px" }}>
                {fieldErrors["accountPrivacy"]}
              </p>
            )}
          </div>
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={!canSubmit}
          aria-busy={acceptInvite.isPending}
        >
          {acceptInvite.isPending ? "Creating account…" : "Create account"}
        </Button>
      </form>
    </AuthShellV2>
  );
}
